import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiFetch, ApiClientError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import type { JourneyDetail, JourneyUpdateInput } from "./types";

export type UpdateJourneyInput = {
  id: string;
  patch: JourneyUpdateInput;
};

type Context = {
  previousDetail: JourneyDetail | undefined;
};

// PATCH /api/admin/journeys/:id
//
// Pattern B field-level optimistic — spreads `patch` into `detail.journey` so
// the cache reflects the in-flight save synchronously. This prevents the
// header-input cursor-jump bug: without `onMutate`, the cache only updated on
// the post-success refetch, and the prop transition fired the
// `useEffect(setLocal(prop), [prop])` in BuilderHeader mid-typing — wiping
// any characters the user added between flush and refetch.
export function useUpdateJourney() {
  const queryClient = useQueryClient();
  return useMutation<{ ok: true }, ApiClientError, UpdateJourneyInput, Context>({
    mutationFn: ({ id, patch }) =>
      apiFetch<{ ok: true }>(`/admin/journeys/${id}`, {
        method: "PATCH",
        body: JSON.stringify(patch),
      }),
    onMutate: async ({ id, patch }) => {
      const detailKey = queryKeys.journeys.detail(id);
      await queryClient.cancelQueries({ queryKey: detailKey });
      const previousDetail = queryClient.getQueryData<JourneyDetail>(detailKey);
      queryClient.setQueryData<JourneyDetail>(detailKey, (old) => {
        if (!old) return old;
        return {
          ...old,
          journey: { ...old.journey, ...patch },
        };
      });
      return { previousDetail };
    },
    onError: (err, { id }, context) => {
      if (context?.previousDetail !== undefined) {
        queryClient.setQueryData(
          queryKeys.journeys.detail(id),
          context.previousDetail,
        );
      }
      toast.error(err.message ?? "Failed to update journey");
    },
    onSettled: (_data, _err, { id }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.journeys.list() });
      queryClient.invalidateQueries({
        queryKey: queryKeys.journeys.detail(id),
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.portal.journeys.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.portal.tasks.all });
    },
  });
}
